import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import NotificationBell from './NotificationBell';
import DecoWordmark, { SIZE_PRESETS as WORDMARK_PRESETS } from './DecoWordmark';
// DEC-1184: circular pre-GA badge beside the wordmark; opens the beta explainer.
import BetaBadge from './BetaBadge';

// DEC-582: Visual marker for platform-internal organizations (Deco Energy).
// Membership in these orgs grants admin privileges (mint beta invites,
// author feature flags) so the badge makes the elevated context obvious.
const internalBadgeStyle = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  padding: '2px 6px',
  borderRadius: '3px',
  background: '#fef3c7',
  color: '#92400e',
  border: '1px solid #fde68a',
};

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { userOrgs, activeOrg, setActiveOrg } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const orgDropdownRef = useRef(null);

  // DEC-1184: one size token drives BOTH the wordmark and the BETA line
  // beneath it, so they can never be sized from different breakpoints.
  const wordmarkSize = isMobile ? 'navbar-mobile' : 'navbar';
  // The wordmark's box runs past its last visible letter by one trailing
  // letter-space PLUS its paddingRight. Derived from DecoWordmark's own preset
  // so a spec change there can't silently detach BETA from the mark.
  const wordmarkPreset = WORDMARK_PRESETS[wordmarkSize];
  const wordmarkTrailingGap =
    parseFloat(wordmarkPreset.letterSpacing) + parseFloat(wordmarkPreset.paddingRight);

  // DEC-714 follow-up: derive a friendlier display name for the personal org
  // so the switcher doesn't show a literal "Personal" that's confusable with
  // a team org happening to be named the same thing. The real org row stays
  // unchanged in the DB; only the UI label is derived from the user.
  const getOrgDisplayName = (org) => {
    if (!org) return '';
    if (org.personal_for_user_id && user?.id && org.personal_for_user_id === user.id) {
      return user.first_name ? `${user.first_name}'s Account` : 'My Account';
    }
    return org.name || '';
  };
  const isPersonalOrg = (org) => !!(org?.personal_for_user_id && user?.id && org.personal_for_user_id === user.id);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close org dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target)) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div style={{
      background: '#FFFFFF',
      borderBottom: '1px solid #E0E0E0',
      padding: isMobile ? '12px 16px' : '16px 40px',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
    }}>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        {/* DECO Logo — adapted variant with tighter letter-spacing so it can
            coexist horizontally with nav links. The standard 16px hero spec
            is preserved on auth/onboarding surfaces where the mark stands
            alone (see BetaAccessGate, AuthModal, SiteSetup).

            DEC-1184: "BETA" is a SUPERSCRIPT to the mark — raised beside it
            the way a trademark symbol sits, rather than stacked beneath it
            (that, the pill, and the circular icon were all rejected in turn).

            alignItems:'flex-start' does the raising: in a row flex the short
            badge aligns to the top of the tall wordmark's line box, which is
            exactly the superscript position. No magic offset to re-tune if
            the wordmark size changes.

            The negative marginLeft is not a fudge — DecoWordmark's box runs
            past its final "O" by one trailing letter-space PLUS its
            paddingRight (24px desktop). A superscript has to hug the mark, so
            that overhang is pulled back, leaving a deliberate 2px gap. Both
            numbers derive from the wordmark's own preset.

            The badge lives OUTSIDE the logo <Link> because it is itself a
            button (it opens the beta explainer): nesting an interactive
            element inside another is invalid HTML and leaves one of the two
            unreachable by keyboard. */}
        <div style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <DecoWordmark size={wordmarkSize} />
          </Link>
          <BetaBadge
            size={wordmarkSize}
            style={{ marginLeft: `${-(wordmarkTrailingGap - 2)}px` }}
          />
        </div>

        {isAuthenticated && (
          <nav style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '8px' }}>
            {/* Desktop nav links - hidden on mobile */}
            {!isMobile && (
              <>
                <Link
                  to="/"
                  style={{
                    color: isActive('/') ? 'var(--navy)' : '#666',
                    textDecoration: 'none',
                    padding: '10px 16px',
                    fontSize: '15px',
                    // Founder 2026-08-09: nav actions speak the condensed
                    // display voice too (it's a Link, so the global
                    // button/header rules don't reach it — set inline).
                    fontFamily: 'var(--font-condensed)',
                    fontStretch: 'condensed',
                    fontWeight: '700',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    background: isActive('/') ? '#f1f5f9' : 'transparent',
                  }}
                >
                  Dashboard
                </Link>
                {/* Beta feedback 2026-05-13: consumer-only "My Sites" + "My
                    Profile" top-nav links removed. Sites are reachable from
                    the Dashboard tile grid; profile lives in the account
                    dropdown ("Manage Profile") to keep the top nav minimal. */}
              </>
            )}
            <NotificationBell />
            {/* DEC-443 + 2026-05-13 nav cleanup: account dropdown holds
                org switcher, Manage Profile, and Logout. Standalone Logout
                button removed in favor of the dropdown menuitem. */}
            {userOrgs.length > 0 && !isMobile && (
              <div ref={orgDropdownRef} style={{ position: 'relative', marginLeft: '8px' }}>
                <button
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  aria-label={`Current context: ${activeOrg ? `${getOrgDisplayName(activeOrg)} (organization)` : 'No organization'}`}
                  aria-expanded={orgDropdownOpen}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0',
                    background: activeOrg ? '#f0f4ff' : '#f8f9fa',
                    color: 'var(--navy)',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span style={{ fontSize: '14px' }}>{activeOrg && isPersonalOrg(activeOrg) ? '\u{1F464}' : '\u{1F3E2}'}</span>
                  {activeOrg ? getOrgDisplayName(activeOrg) : 'Loading...'}
                  {activeOrg?.is_internal && (
                    <span style={internalBadgeStyle}>Internal</span>
                  )}
                  <span style={{ fontSize: '10px', opacity: 0.6 }}>{orgDropdownOpen ? '\u25B2' : '\u25BC'}</span>
                </button>

                {orgDropdownOpen && (
                  <div role="menu" style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '6px',
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    minWidth: '240px',
                    zIndex: 1001,
                    overflow: 'hidden',
                  }}>
                    {/* DEC-714 follow-up: virtual "My account" entry FULLY HIDDEN. The
                        previous `hidden={true}` was overridden by the inline display:flex.
                        Forcing display:none here, plus omitting the divider below. */}
                    <button
                      role="menuitem"
                      hidden={true}
                      style={{ display: 'none' }}
                    >
                      <span style={{ fontSize: '16px' }}>{'\u{1F464}'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: !activeOrg ? '600' : '400' }}>My account</div>
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>Personal — not in any organization</div>
                      </div>
                      {!activeOrg && <span style={{ marginLeft: 'auto', color: '#22c55e', fontSize: '12px' }}>{'\u2713'}</span>}
                    </button>

                    <div style={{ borderTop: '1px solid #f0f0f0' }} />

                    {userOrgs.map(org => (
                      <button
                        key={org.id}
                        role="menuitem"
                        onClick={() => { setActiveOrg(org); setOrgDropdownOpen(false); navigate('/'); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                          padding: '12px 16px', border: 'none',
                          background: activeOrg?.id === org.id ? '#f0f4ff' : 'white',
                          cursor: 'pointer', fontSize: '14px', color: 'var(--navy)', textAlign: 'left',
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>{isPersonalOrg(org) ? '\u{1F464}' : '\u{1F3E2}'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: activeOrg?.id === org.id ? '600' : '400', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {getOrgDisplayName(org)}
                            {org.is_internal && <span style={internalBadgeStyle}>Internal</span>}
                          </div>
                          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'capitalize' }}>{isPersonalOrg(org) ? 'Personal' : org.role}</div>
                        </div>
                        {activeOrg?.id === org.id && <span style={{ color: '#22c55e', fontSize: '12px' }}>{'\u2713'}</span>}
                      </button>
                    ))}

                    <div style={{ borderTop: '1px solid #f0f0f0' }} />

                    <button
                      role="menuitem"
                      onClick={() => { navigate('/organizations'); setOrgDropdownOpen(false); }}
                      style={{
                        display: 'block', width: '100%', padding: '10px 16px', border: 'none',
                        background: 'white', cursor: 'pointer', fontSize: '13px',
                        color: '#666', textAlign: 'left',
                      }}
                    >
                      Manage Organizations
                    </button>
                    {/* Beta feedback 2026-05-13: Manage Profile lives here now
                        (was a separate top-nav link). Same visual treatment as
                        Manage organizations so the two read as a pair. */}
                    <button
                      role="menuitem"
                      onClick={() => { navigate('/profile'); setOrgDropdownOpen(false); }}
                      style={{
                        display: 'block', width: '100%', padding: '10px 16px', border: 'none',
                        background: 'white', cursor: 'pointer', fontSize: '13px',
                        color: '#666', textAlign: 'left',
                      }}
                    >
                      Manage Profile
                    </button>

                    {/* DEC-720 + DEC-723 follow-up: single Admin settings link
                        for internal-org members. The /admin page hosts the
                        Internal team / Beta program / Feature flags panels in
                        a tabbed view — keeps the org dropdown uncluttered.
                        Styled identically to Manage Organizations / Manage
                        Profile so it reads as a peer management link. */}
                    {userOrgs.some(o => o.is_internal) && (
                      <button
                        role="menuitem"
                        onClick={() => { navigate('/admin'); setOrgDropdownOpen(false); }}
                        style={{
                          display: 'block', width: '100%', padding: '10px 16px', border: 'none',
                          background: 'white', cursor: 'pointer', fontSize: '13px',
                          color: '#666', textAlign: 'left',
                        }}
                      >
                        Admin Settings
                      </button>
                    )}

                    {/* Beta feedback 2026-05-13: Logout lives at the bottom of
                        the account dropdown (was a standalone top-nav button).
                        Visual separator above so it reads as a terminal action,
                        not a sibling of the management links. Sky-blue color
                        keeps it identifiable as an action while still scoped
                        inside the menu. */}
                    <div style={{ borderTop: '1px solid #f0f0f0' }} />
                    <button
                      role="menuitem"
                      onClick={() => { handleLogout(); setOrgDropdownOpen(false); }}
                      style={{
                        display: 'block', width: '100%', padding: '12px 16px', border: 'none',
                        background: 'white', cursor: 'pointer', fontSize: '13px',
                        color: 'var(--sky-blue)', fontWeight: 600, textAlign: 'left',
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </nav>
        )}
      </div>
    </div>
  );
};

export default Navbar;
